/**
 * "Skin Reading with Leroy" — results dashboard.
 *
 * Renders a real AIAnalysisResult from the assessments API: the score ring,
 * skin-metric bars, detected-concern chips with confidence, and the prescribed
 * regimen. Nothing here is fixture data.
 *
 * Two mount points share this component: inline at the end of the quiz, and on
 * the assessment detail page so a reading stays revisitable.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, RotateCcw, Sparkles } from 'lucide-react';
import type { AIAnalysisResult } from '@/types/member';
import { products as catalogue, Product } from '@/data/products';
import { useShop } from '@/context/ShopContext';

const LIME = '#BDD630';
const LIME_LIGHT = '#c5e47a';
const GOLD = '#F4C95D';
const TERRACOTTA = '#E07A5F';

const stageBg: React.CSSProperties = {
  background: 'linear-gradient(135deg, #1a3d2a 0%, #12220c 100%)',
  minHeight: '100vh',
};
const glass: React.CSSProperties = {
  background: 'rgba(255,255,255,.05)',
  border: '1px solid rgba(255,255,255,.12)',
  borderRadius: 16,
};

/** Metric colour follows the design's thresholds. Shared with the printable report. */
export function metricColor(v: number) {
  if (v < 50) return TERRACOTTA;
  if (v < 70) return GOLD;
  return LIME;
}

/** 'pore_size' -> 'Pore size'. The backend returns whatever the model produced,
 *  so we humanise keys rather than assuming a fixed set of metrics. */
export function humanise(key: string) {
  const s = key.replace(/[_-]+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function AnimBar({ value, delay = 0, color }: { value: number; delay?: number; color: string }) {
  const [w, setW] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setW(value), 120 + delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return (
    <div style={{ height: 8, borderRadius: 999, background: 'rgba(255,255,255,.1)', overflow: 'hidden' }}>
      <div
        style={{
          height: '100%',
          width: `${w}%`,
          background: color,
          borderRadius: 999,
          transition: 'width 1.1s cubic-bezier(.4,0,.2,1)',
        }}
      />
    </div>
  );
}

function ScoreRing({ score }: { score: number }) {
  const [p, setP] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setP(score), 200);
    return () => clearTimeout(t);
  }, [score]);
  return (
    <div style={{ position: 'relative', width: 176, height: 176 }}>
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: `conic-gradient(${LIME} ${p * 3.6}deg, rgba(255,255,255,.12) 0deg)`,
          transition: 'background 1.4s cubic-bezier(.4,0,.2,1)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 14,
          borderRadius: '50%',
          background: '#12220c',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span className="font-serif" style={{ fontSize: 52, fontWeight: 700, color: '#fff', lineHeight: 1 }}>
          {score}
        </span>
        <span
          className="font-display"
          style={{ textTransform: 'uppercase', letterSpacing: '.1em', fontSize: 10, color: LIME_LIGHT, marginTop: 4 }}
        >
          Skin Score
        </span>
      </div>
    </div>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="font-display"
      style={{ textTransform: 'uppercase', letterSpacing: '.08em', fontSize: 12, color: LIME_LIGHT, marginBottom: 16 }}
    >
      {children}
    </div>
  );
}

/**
 * The AI returns advice ({category, recommendation, key_ingredients, reason}),
 * not a shoppable SKU. Match it to the real catalogue so the regimen can
 * actually be added to the cart; where nothing matches we still show the
 * advice, just without a buy button, rather than inventing a product.
 */
function matchProduct(recommendation: string, category: string): Product | undefined {
  const hay = `${recommendation} ${category}`.toLowerCase();
  const tokens = hay.split(/[^a-z]+/).filter(t => t.length > 3);

  let best: { product: Product; score: number } | undefined;
  for (const p of catalogue) {
    const target = `${p.name} ${p.category} ${p.subcategory ?? ''} ${p.keyIngredients.join(' ')}`.toLowerCase();
    let score = 0;
    for (const t of tokens) if (target.includes(t)) score += 1;
    if (score > 0 && (!best || score > best.score)) best = { product: p, score };
  }
  return best?.product;
}

export interface SkinReadingResultsProps {
  result: AIAnalysisResult;
  /** Where "Retake reading" should go / do. Omitted hides the button. */
  onRetake?: () => void;
}

export function SkinReadingResults({ result, onRetake }: SkinReadingResultsProps) {
  const { addToCart } = useShop();
  const [added, setAdded] = useState<Record<string, boolean>>({});

  const score = Math.round(result.overall_score ?? 0);

  const metrics = useMemo(
    () =>
      Object.entries(result.skin_metrics ?? {}).map(([key, m]) => ({
        key,
        label: humanise(key),
        value: Math.max(0, Math.min(100, Math.round(m?.score ?? 0))),
        status: m?.status ?? '',
      })),
    [result.skin_metrics]
  );

  const concerns = useMemo(
    () =>
      (result.skin_conditions ?? []).map(c => ({
        label: c.condition,
        // API confidence is 0–1; the design shows a percentage.
        conf: Math.round((c.confidence ?? 0) * 100),
        severity: c.severity,
      })),
    [result.skin_conditions]
  );

  const regimen = useMemo(
    () =>
      (result.recommended_products ?? []).map(rp => ({
        ...rp,
        product: matchProduct(rp.recommendation, rp.category),
      })),
    [result.recommended_products]
  );

  const shoppable = regimen.filter(r => r.product) as (typeof regimen[number] & { product: Product })[];
  const regimenTotal = shoppable.reduce((sum, r) => sum + r.product.price, 0);

  const add = (p: Product) => {
    addToCart(p, 1);
    setAdded(prev => ({ ...prev, [p.id]: true }));
    setTimeout(() => setAdded(prev => ({ ...prev, [p.id]: false })), 2000);
  };

  return (
    <div style={{ ...stageBg, padding: '48px 28px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span
            className="font-display"
            style={{
              width: 30, height: 30, borderRadius: '50%', background: LIME, color: '#1A2E12',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
            }}
          >
            L
          </span>
          <span
            className="font-display"
            style={{ textTransform: 'uppercase', letterSpacing: '.1em', fontSize: 12, color: LIME_LIGHT }}
          >
            Your Skin Reading · by Leroy
          </span>
        </div>
        <h1 className="font-serif normal-case" style={{ fontSize: 42, color: '#fff', margin: '0 0 28px', lineHeight: 1.1 }}>
          {result.skin_type ? `${humanise(result.skin_type)} skin` : 'Your skin reading'}
        </h1>

        {/* Score + metrics */}
        <div className="grid gap-[22px] items-start grid-cols-1 lg:grid-cols-[320px_1fr]">
          <div style={{ ...glass, padding: 28 }} className="flex flex-col items-center text-center">
            <ScoreRing score={score} />
            {result.findings_summary && (
              <p style={{ color: 'rgba(255,255,255,.75)', fontSize: 14, lineHeight: 1.6, margin: '20px 0 0' }}>
                {result.findings_summary}
              </p>
            )}
          </div>

          <div style={{ ...glass, padding: 28 }}>
            <Eyebrow>Skin metrics</Eyebrow>
            {metrics.length === 0 ? (
              <p style={{ color: 'rgba(255,255,255,.55)', fontSize: 14 }}>
                No per-metric breakdown was returned for this reading.
              </p>
            ) : (
              <div className="flex flex-col gap-[18px]">
                {metrics.map((m, i) => (
                  <div key={m.key}>
                    <div className="flex justify-between mb-[7px]">
                      <span style={{ fontSize: 14, color: '#fff', fontWeight: 600 }}>{m.label}</span>
                      <span className="font-display" style={{ fontSize: 14, color: LIME }}>{m.value}%</span>
                    </div>
                    <AnimBar value={m.value} delay={i * 120} color={metricColor(m.value)} />
                    {m.status && (
                      <p style={{ fontSize: 12, color: 'rgba(255,255,255,.55)', margin: '6px 0 0' }}>{m.status}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Detected concerns */}
        {concerns.length > 0 && (
          <div style={{ ...glass, padding: 28, marginTop: 22 }}>
            <Eyebrow>Detected concerns</Eyebrow>
            <div className="flex flex-wrap gap-3">
              {concerns.map(c => (
                <div
                  key={c.label}
                  className="flex items-center gap-[10px]"
                  style={{
                    background: 'rgba(255,255,255,.05)',
                    border: '1px solid rgba(255,255,255,.12)',
                    borderRadius: 999,
                    padding: '9px 16px',
                  }}
                >
                  <span style={{ fontSize: 14, color: '#fff' }}>{c.label}</span>
                  <span
                    className="font-display"
                    style={{ fontSize: 12, color: '#1A2E12', background: LIME, borderRadius: 999, padding: '2px 9px', fontWeight: 700 }}
                  >
                    {c.conf}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Prescribed regimen */}
        {regimen.length > 0 && (
          <div style={{ marginTop: 34 }}>
            <div className="flex items-end justify-between flex-wrap gap-3 mb-[18px]">
              <div>
                <div
                  className="font-display"
                  style={{ textTransform: 'uppercase', letterSpacing: '.08em', fontSize: 12, color: LIME_LIGHT }}
                >
                  Your prescribed regimen
                </div>
                <h2 className="font-serif normal-case" style={{ fontSize: 30, color: '#fff', margin: '4px 0 0' }}>
                  {regimen.length} step{regimen.length === 1 ? '' : 's'}, built for your reading
                </h2>
              </div>
              {shoppable.length > 0 && (
                <button
                  onClick={() => shoppable.forEach(r => add(r.product))}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-transform hover:scale-105"
                  style={{ background: LIME, color: '#1A2E12' }}
                >
                  Add all to cart · R{regimenTotal.toFixed(2)}
                </button>
              )}
            </div>

            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
              {regimen.map((r, i) => (
                <div key={`${r.category}-${i}`} className="bg-white rounded-2xl overflow-hidden flex flex-col">
                  <div style={{ background: '#F7F5EE', padding: '10px 16px' }}>
                    <span
                      className="font-display"
                      style={{
                        background: '#1A2E12', color: '#fff', fontSize: 10, fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '.05em', padding: '4px 9px', borderRadius: 999,
                      }}
                    >
                      Step {i + 1}
                    </span>
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="font-display font-semibold text-[15px] mb-0.5" style={{ color: '#1A2E12' }}>
                      {r.product?.name ?? r.recommendation}
                    </div>
                    <div className="text-xs mb-2" style={{ color: '#6B7B6E' }}>{humanise(r.category)}</div>
                    {r.reason && (
                      <p className="text-xs mb-3 flex-1" style={{ color: '#6B7B6E', lineHeight: 1.5 }}>{r.reason}</p>
                    )}
                    {r.product ? (
                      <div className="flex items-center justify-between mt-auto">
                        <span className="font-bold" style={{ color: '#567D38' }}>R{r.product.price.toFixed(2)}</span>
                        <button
                          onClick={() => add(r.product!)}
                          disabled={added[r.product.id]}
                          className="px-4 py-1.5 rounded-full text-sm font-semibold transition-colors"
                          style={{
                            background: added[r.product.id] ? '#567D38' : 'rgba(189,214,48,.2)',
                            color: added[r.product.id] ? '#fff' : '#567D38',
                          }}
                        >
                          {added[r.product.id] ? 'Added' : 'Add'}
                        </button>
                      </div>
                    ) : (
                      /* No catalogue match — show the advice, don't fake a product. */
                      <p className="text-xs mt-auto pt-2" style={{ color: '#6B7B6E' }}>
                        {r.key_ingredients?.length
                          ? `Look for: ${r.key_ingredients.slice(0, 3).join(', ')}`
                          : 'Not currently stocked'}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTAs */}
        <div className="flex gap-[14px] justify-center flex-wrap" style={{ marginTop: 40 }}>
          <Link
            to="/regimen"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold transition-transform hover:scale-105"
            style={{ background: LIME, color: '#1A2E12' }}
          >
            Track this regimen <ArrowRight className="w-[18px] h-[18px]" />
          </Link>
          {onRetake && (
            <button
              onClick={onRetake}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold border-2 transition-colors hover:bg-white/5"
              style={{ borderColor: 'rgba(255,255,255,.3)', color: '#fff' }}
            >
              <RotateCcw className="w-[18px] h-[18px]" /> Retake reading
            </button>
          )}
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold transition-colors hover:bg-white/5"
            style={{ color: 'rgba(255,255,255,.75)' }}
          >
            <Sparkles className="w-[18px] h-[18px]" /> Browse the shop
          </Link>
        </div>
      </div>
    </div>
  );
}

export default SkinReadingResults;
